'use client';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import type { NetworkCaptureEntry } from '@campaign-manager/feature/model/har/types';

import { JsonViewer } from '../JsonViewer';

// Convert NetworkCaptureEntry to our expected format
function convertNetworkCaptureEntry(entry: NetworkCaptureEntry, index: number) {
  return {
    id: entry.id || `entry-${index}`,
    timestamp: entry.startTime || Date.now(),
    method: entry.method,
    url: entry.url,
    path: extractPath(entry.url),
    status: entry.response?.status,
    duration: entry.endTime ? entry.endTime - entry.startTime : undefined,
    requestHeaders: entry.requestHeaders || {},
    requestBody: entry.requestBody,
    responseHeaders: entry.responseHeaders || {},
    responseBody: entry.responseBody,
    error: entry.error,
  };
}

function extractPath(url: string): string {
  try {
    const urlObj = new URL(url);
    return urlObj.pathname + urlObj.search;
  } catch {
    return url;
  }
}

function getReadableRequestName(url: string, method: string): string {
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/').filter(Boolean);

    // Extract the main API endpoint parts
    // e.g. /api/rmt/v1/placements -> "placements"
    if (pathParts.includes('api') && pathParts.includes('rmt')) {
      const apiIndex = pathParts.indexOf('rmt');
      const relevantParts = pathParts.slice(apiIndex + 1);

      // Skip version if present
      if (relevantParts[0] && relevantParts[0].startsWith('v')) {
        relevantParts.shift();
      }

      // Get the main resource
      const resource = relevantParts[0] || 'unknown';
      const resourceId = relevantParts[1];

      // Build readable name
      let name = `${method} ${resource}`;

      // Add ID if present and not too long
      if (resourceId && resourceId.length < 20 && !resourceId.includes('?')) {
        name += `/${resourceId}`;
      }

      // Add query parameter hints
      const searchParams = urlObj.searchParams;
      const paramHints: string[] = [];

      if (searchParams.has('filter')) {
        paramHints.push('filtered');
      }
      if (searchParams.has('sort') || searchParams.has('order_by')) {
        paramHints.push('sorted');
      }
      if (searchParams.has('limit') || searchParams.has('page_size')) {
        paramHints.push('paginated');
      }
      if (searchParams.has('search') || searchParams.has('q')) {
        paramHints.push('searched');
      }

      if (paramHints.length > 0) {
        name += ` (${paramHints.join(', ')})`;
      }

      return name;
    }

    // Fallback for non-standard API paths
    const lastPart = pathParts[pathParts.length - 1] || 'root';
    return `${method} ${lastPart}`;
  } catch {
    return `${method} request`;
  }
}

function getShortUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    const path = urlObj.pathname;
    const search = urlObj.search;

    // Always truncate very long URLs
    const fullPath = path + search;
    if (fullPath.length > 80) {
      if (search.length > 20) {
        return `${path}?...`;
      }
      // If path itself is too long, truncate it
      if (path.length > 60) {
        const pathParts = path.split('/');
        return `${pathParts.slice(0, -1).join('/')}/...${search}`;
      }
    }

    return fullPath;
  } catch {
    // Fallback for invalid URLs - truncate if too long
    if (url.length > 80) {
      return `${url.substring(0, 77)}...`;
    }
    return url;
  }
}

interface ApiCallData {
  id: string;
  timestamp: number;
  method: string;
  url: string;
  path: string;
  status?: number;
  duration?: number;
  requestHeaders?: Record<string, string>;
  requestBody?: any;
  responseHeaders?: Record<string, string>;
  responseBody?: any;
  error?: any;
}

interface ApiContextModalProps {
  open: boolean;
  onClose: () => void;
  calls: NetworkCaptureEntry[];
  onClear?: () => void;
  onExport?: () => string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div
      hidden={value !== index}
      style={{ height: '100%', overflow: 'auto' }}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function formatDuration(ms?: number): string {
  if (!ms) return '-';
  if (ms < 1000) return `${(ms).toFixed(0)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function getStatusColor(status?: number): 'success' | 'error' | 'warning' | 'default' {
  if (!status) return 'default';
  if (status >= 200 && status < 300) return 'success';
  if (status >= 400 && status < 500) return 'warning';
  if (status >= 500) return 'error';
  return 'default';
}

function getMethodColor(
  method: string,
): 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' {
  switch (method) {
    case 'GET':
      return 'primary';
    case 'POST':
      return 'success';
    case 'PUT':
      return 'warning';
    case 'PATCH':
      return 'info';
    case 'DELETE':
      return 'error';
    default:
      return 'secondary';
  }
}

export default function ApiContextModal({
  open,
  onClose,
  calls,
  onClear,
  onExport,
}: ApiContextModalProps) {
  const [tabValue, setTabValue] = useState(0);
  const [expandedCall, setExpandedCall] = useState<string | false>(false);
  const [expandedEntity, setExpandedEntity] = useState<string | false>(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Convert NetworkCaptureEntry to our expected format
  const convertedCalls: ApiCallData[] = calls.map(convertNetworkCaptureEntry);

  const handleAccordionChange =
    (callId: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedCall(isExpanded ? callId : false);
    };

  const handleEntityAccordionChange =
    (entityType: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedEntity(isExpanded ? entityType : false);
    };

  const handleCopyToClipboard = (data: any, format: 'json' | 'curl' = 'json') => {
    let text = '';

    if (format === 'json') {
      text = JSON.stringify(data, null, 2);
    } else if (format === 'curl' && data.url) {
      // Generate curl command
      const headers = data.requestHeaders || {};
      let curl = `curl -X ${data.method} '${data.url}'`;

      Object.entries(headers).forEach(([key, value]) => {
        curl += ` \\\n  -H '${key}: ${value}'`;
      });

      if (data.requestBody) {
        curl += ` \\\n  -d '${JSON.stringify(data.requestBody)}'`;
      }

      text = curl;
    }

    navigator.clipboard.writeText(text);
  };

  const handleExport = () => {
    if (onExport) {
      const data = onExport();
      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `api-context-${new Date().toISOString()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const aggregatedData = convertedCalls.reduce(
    (acc, call) => {
      if (!call.responseBody) return acc;

      try {
        // Parse response body if it's a string
        let parsedResponse = call.responseBody;
        if (typeof call.responseBody === 'string') {
          parsedResponse = JSON.parse(call.responseBody);
        }

        // Extract entity type from URL
        let urlObj: URL;
        let pathParts: string[];

        try {
          // Handle both absolute and relative URLs
          urlObj = call.url.startsWith('http')
            ? new URL(call.url)
            : new URL(call.url, window.location.origin);
          pathParts = urlObj.pathname.split('/').filter(Boolean);
        } catch {
          // Fallback: parse path directly if URL parsing fails
          const path = call.url.startsWith('/') ? call.url : `/${call.url}`;
          pathParts = path.split('/').filter(Boolean);
        }

        let entityType = 'unknown';
        if (pathParts.includes('api') && pathParts.includes('rmt')) {
          const rmtIndex = pathParts.indexOf('rmt');
          const relevantParts = pathParts.slice(rmtIndex + 1);

          // Skip version if present
          if (relevantParts[0] && relevantParts[0].startsWith('v')) {
            relevantParts.shift();
          }

          entityType = relevantParts[0] || 'unknown';
        }

        if (!acc[entityType]) {
          acc[entityType] = [];
        }

        // Add response data
        if (Array.isArray(parsedResponse)) {
          acc[entityType].push(...parsedResponse);
        } else if (parsedResponse.data && Array.isArray(parsedResponse.data)) {
          acc[entityType].push(...parsedResponse.data);
        } else if (typeof parsedResponse === 'object') {
          acc[entityType].push(parsedResponse);
        }
      } catch (error) {
        console.warn('[ApiContextModal] Failed to parse response body:', error);
        if (!acc.unknown) {
          acc.unknown = [];
        }
        // Store the raw response as fallback
        acc.unknown.push({
          _raw: call.responseBody,
          _url: call.url,
          _method: call.method,
          _parseError: error instanceof Error ? error.message : String(error),
        });
      }

      return acc;
    },
    {} as Record<string, any[]>,
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isFullscreen ? false : 'lg'}
      fullWidth
      fullScreen={isFullscreen}
      slotProps={{
        paper: {
          sx: {
            height: isFullscreen ? '100vh' : '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      <DialogTitle>
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6">API Context - Page Data</Typography>
          <Stack
            direction="row"
            spacing={1}
          >
            <IconButton
              onClick={() => setIsFullscreen(!isFullscreen)}
              size="small"
              title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
            <IconButton
              onClick={handleExport}
              size="small"
              title="Export all data"
            >
              <DownloadIcon />
            </IconButton>
            {onClear && (
              <IconButton
                onClick={onClear}
                size="small"
                title="Clear captured data"
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Stack>
        </Stack>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabValue}
          onChange={(_, value) => setTabValue(value)}
        >
          <Tab label={`Raw Calls (${convertedCalls.length})`} />
          <Tab label="By entity" />
        </Tabs>
      </Box>

      <DialogContent sx={{ flex: 1, overflow: 'hidden', p: 0 }}>
        <TabPanel
          value={tabValue}
          index={0}
        >
          <Box sx={{ px: 3, pb: 2, height: '100%', overflow: 'auto' }}>
            {convertedCalls.length === 0 ? (
              <Alert severity="info">
                No API calls captured yet. Navigate through the application to capture API activity.
              </Alert>
            ) : (
              convertedCalls.map((call) => (
                <Accordion
                  key={call.id}
                  expanded={expandedCall === call.id}
                  onChange={handleAccordionChange(call.id)}
                  sx={{ mb: 1 }}
                  elevation={0}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      '& .MuiAccordionSummary-content': {
                        margin: '8px 0',
                        alignItems: 'center',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        width: '100%',
                        minWidth: 0, // Important: allows flex children to shrink
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          flexShrink: 0, // Keep chips from shrinking
                        }}
                      >
                        <Chip
                          label={call.method}
                          size="small"
                          color={getMethodColor(call.method)}
                          sx={{ minWidth: 60, fontWeight: 'bold' }}
                        />
                        <Chip
                          label={call.status || 'pending'}
                          size="small"
                          color={getStatusColor(call.status)}
                          sx={{ minWidth: 60 }}
                        />
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0, // Important: allows text to shrink and truncate
                          overflow: 'hidden',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: 'text.primary',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%',
                          }}
                        >
                          {getReadableRequestName(call.url, call.method)}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            color: 'text.secondary',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                            width: '100%',
                          }}
                        >
                          {getShortUrl(call.url)}
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontWeight: 500,
                          minWidth: 80,
                          textAlign: 'right',

                          // Prevent duration from shrinking
                          flexShrink: 0,
                        }}
                      >
                        {formatDuration(call.duration)}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ bgcolor: 'grey.50' }}>
                    <Stack spacing={3}>
                      {/* Request Section */}
                      <Box>
                        <Stack
                          direction="row"
                          sx={{
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 1.5,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, color: 'text.primary' }}
                          >
                            📤 Request
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleCopyToClipboard(call, 'curl')}
                            sx={{ color: 'text.secondary' }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Stack>

                        {/* Request URL and Method */}
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, mb: 0.5 }}
                          >
                            URL
                          </Typography>
                          <Box
                            sx={{
                              bgcolor: 'background.paper',
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              p: 1.5,
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.875rem',
                                wordBreak: 'break-all',
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  color:
                                    getMethodColor(call.method) === 'primary'
                                      ? 'primary.main'
                                      : 'success.main',
                                  fontWeight: 'bold',
                                }}
                              >
                                {call.method}
                              </Box>{' '}
                              {call.url}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Request Headers */}
                        {call.requestHeaders && Object.keys(call.requestHeaders).length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500, mb: 0.5 }}
                            >
                              Headers
                            </Typography>
                            <Box
                              sx={{
                                bgcolor: 'background.paper',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 1.5,
                                maxHeight: 200,
                                overflow: 'auto',
                              }}
                            >
                              <Typography
                                component="pre"
                                sx={{
                                  fontFamily: 'monospace',
                                  fontSize: '0.8rem',
                                  margin: 0,
                                  whiteSpace: 'pre-wrap',
                                }}
                              >
                                {JSON.stringify(call.requestHeaders, null, 2)}
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {/* Request Body */}
                        {call.requestBody && (
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500, mb: 0.5 }}
                            >
                              Body
                            </Typography>
                            <Box
                              sx={{
                                bgcolor: 'background.paper',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 1.5,
                                maxHeight: 300,
                                overflow: 'auto',
                              }}
                            >
                              <Typography
                                component="pre"
                                sx={{
                                  fontFamily: 'monospace',
                                  fontSize: '0.8rem',
                                  margin: 0,
                                  whiteSpace: 'pre-wrap',
                                }}
                              >
                                {typeof call.requestBody === 'string'
                                  ? call.requestBody
                                  : JSON.stringify(call.requestBody, null, 2)}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>

                      {/* Response Section */}
                      {(call.responseBody || call.error) && (
                        <Box>
                          <Stack
                            direction="row"
                            sx={{
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              mb: 1.5,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                color: call.error ? 'error.main' : 'success.main',
                              }}
                            >
                              📥 Response {call.status && `(${call.status})`}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleCopyToClipboard(call.responseBody || call.error)}
                              sx={{ color: 'text.secondary' }}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Stack>

                          <Box
                            sx={{
                              bgcolor: 'background.paper',
                              border: '2px solid',
                              borderColor: call.error ? 'error.main' : 'success.main',
                              borderRadius: 2,
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                p: 1.5,
                                maxHeight: 500,
                                overflow: 'auto',
                              }}
                            >
                              <Typography
                                component="pre"
                                sx={{
                                  fontFamily: 'monospace',
                                  fontSize: '0.8rem',
                                  margin: 0,
                                  whiteSpace: 'pre-wrap',
                                  lineHeight: 1.4,
                                }}
                              >
                                {JSON.stringify(call.error || call.responseBody, null, 2)}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </Box>
        </TabPanel>

        <TabPanel
          value={tabValue}
          index={1}
        >
          <Box sx={{ px: 3, pb: 2, height: '100%', overflow: 'auto' }}>
            {Object.keys(aggregatedData).length === 0 ? (
              <Alert
                severity="info"
                sx={{ mt: 2 }}
              >
                No response data to aggregate yet. Navigate through the application to capture API
                responses.
              </Alert>
            ) : (
              <Stack
                spacing={2}
                sx={{ mt: 2 }}
              >
                {Object.entries(aggregatedData).map(([entityType, data]) => (
                  <Accordion
                    key={entityType}
                    expanded={expandedEntity === entityType}
                    onChange={handleEntityAccordionChange(entityType)}
                    sx={{
                      mb: 1,
                      transform: expandedEntity === entityType ? 'scale(1.02)' : 'scale(1)',
                      transition: 'transform 0.2s ease-in-out',
                      zIndex: expandedEntity === entityType ? 10 : 1,
                    }}
                    elevation={expandedEntity === entityType ? 8 : 1}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        '& .MuiAccordionSummary-content': {
                          margin: '12px 0',
                          alignItems: 'center',
                        },
                        bgcolor: 'primary.light',
                        color: 'primary.contrastText',
                        '&.Mui-expanded': {
                          bgcolor: 'primary.main',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%',
                          pr: 2,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            textTransform: 'capitalize',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          {entityType.replace('-', ' ')} ({data.length} items)
                        </Typography>
                        <Box
                          component="span"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyToClipboard(data);
                          }}
                          sx={{
                            color: 'inherit',
                            opacity: 0.8,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                            borderRadius: 1,
                            '&:hover': {
                              opacity: 1,
                              bgcolor: 'rgba(255, 255, 255, 0.1)',
                            },
                          }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        bgcolor: 'background.default',
                        p: 3,
                      }}
                    >
                      <JsonViewer
                        data={data}
                        maxInitialDepth={2}
                        showCopyButton={false}
                        compact={false}
                      />
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            )}
          </Box>
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
